/**
 * balanced-events.js - what people actually do on the storefront.
 *
 * Every event goes two places: Shopify's customer-events bus as `balanced:<name>`, so any
 * pixel added later can subscribe without a theme change, and Microsoft Clarity as a custom
 * event, which is what makes a recording findable. Clarity has no API for custom events, so
 * these are for narrowing the recording list, not for counting. Counting is Shopify's job.
 *
 * It listens to the theme's own event bus wherever one exists rather than scraping the DOM,
 * because Atelier's pub/sub survives a theme update and a CSS selector does not. Where no
 * native event exists the hook is a stable attribute, never a class name that a redesign
 * would rename.
 *
 * Nothing here sends personal data. Search records the length of a query, never the query.
 * The quiz records that an email was submitted, never the address.
 *
 * Launch Checklist item 39. Contract and event dictionary in Operations/Measurement Plan.
 */

const B = window.Balanced;

/** Wrap every listener: one broken hook must not take the rest of the file down. */
const safe = (label, fn) => (...args) => {
  try {
    return fn(...args);
  } catch (err) {
    if (B?.cfg?.debug) console.warn('[balanced] ' + label + ' failed', err);
  }
};

const on = (target, type, fn, opts) =>
  target && target.addEventListener(type, safe(type, fn), opts);

/* ------------------------------------------------------------------ consent */

/**
 * Run `go` once tracking is allowed.
 *
 * Default is Legal Register decision D-e: analytics and recording run on entry, advertising
 * stays gated. Turning on "Wait for analytics consent" in the theme editor switches to the
 * strict reading of the privacy policy with no code change, which is the whole point of the
 * setting: the posture is Erez's to change, not a developer's.
 */
function whenAllowed(go) {
  if (!B.cfg.consent) return go();
  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const arm = () => {
    if (allowed()) return go();
    on(document, 'visitorConsentCollected', () => allowed() && go(), { once: true });
  };
  if (window.Shopify?.customerPrivacy) return arm();
  window.Shopify?.loadFeatures?.(
    [{ name: 'consent-tracking-api', version: '0.1' }],
    (err) => { if (!err) arm(); }
  );
}

/* ------------------------------------------------------------------ clarity */

function loadClarity(id) {
  if (!id || window.clarity) return;
  window.clarity = function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.clarity.ms/tag/' + encodeURIComponent(id);
  document.head.appendChild(s);
}

/* ------------------------------------------------------------------ source */

/**
 * Stamp the referral code on the session so a recording can be filtered by it.
 *
 * Clarity reads utm_source into its own Source dimension by itself; this tag is what keeps
 * the code attached after the visitor clicks through to a second page, and it is sanitised to
 * the snake_case shape Traffic Sources uses so a junk parameter cannot invent a source.
 */
function source() {
  const KEY = 'balanced.source';
  let code = '';
  try { code = sessionStorage.getItem(KEY) || ''; } catch (e) { /* private mode */ }
  const raw = linkCode(new URL(location.href))
    .toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 32);
  if (raw && !code) {
    code = raw;
    try { sessionStorage.setItem(KEY, code); } catch (e) { /* private mode */ }
  }
  B.tag('source', code || 'direct');
  B.tag('template', B.cfg.template || '');
  /* Facebook adds fbclid to every link tapped out of it, so this marks a Facebook visit even
     when the code was lost. */
  if (new URL(location.href).searchParams.has('fbclid')) B.tag('via', 'facebook');
}

/**
 * The code from a link as it was actually sent, not only as bin/link writes it (2026-10-07,
 * CP-02). Facebook posts went out as `?utm=CODE` and as `/#video/?utm_source=CODE`. Clarity's
 * own Source dimension reads neither and Shopify cannot see past the #, but this tag can.
 */
function linkCode(url) {
  const q = url.searchParams;
  let raw = q.get('utm_source') || q.get('utm') || '';
  const i = url.hash.indexOf('?');
  if (!raw && i !== -1) {
    const h = new URLSearchParams(url.hash.slice(i + 1));
    raw = h.get('utm_source') || h.get('utm') || '';
  }
  return raw;
}

/* ------------------------------------------------------------------ sections */

/** Which of Erez's sections a visitor actually reached, once each per pageview. */
function sections() {
  const seen = new Set();
  const marks = document.querySelectorAll('[data-track-section]');
  if (!marks.length || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const name = e.target.getAttribute('data-track-section');
      if (name && !seen.has(name)) {
        seen.add(name);
        B.track('section_seen', { section: name });
      }
      io.unobserve(e.target);
    });
  }, { threshold: 0.3 });
  marks.forEach((m) => io.observe(m));

  /* The measuring guide and the size table are two different behaviours: the guide is the
     instruction to measure, the table is the lookup. Someone can scroll past the table to the
     CTA without ever reading the guide, so counting only one of them misleads. */
  [['figure.fit__guide', 'size_guide_seen'], ['.fit__table', 'size_table_seen']]
    .forEach(([sel, name]) => {
      const el = document.querySelector(sel);
      if (!el) return;
      const o = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { B.track(name, {}); o.disconnect(); }
      }, { threshold: 0.4 });
      o.observe(el);
    });
}

/* ------------------------------------------------------------------ clicks */

/**
 * Links inside Erez's sections.
 *
 * The four product CTAs point at the same target and three share a label, so the section is
 * the only thing that separates them. That separation is the point: the page repeats a CTA
 * after every block so we can learn which argument moves someone to the buy box. The fifth,
 * .guide__cta, is on the homepage and points at the quiz rather than the buy box, added
 * 2026-09-18; it reports as from=guide.
 */
function clicks() {
  on(document, 'click', (e) => {
    const a = e.target.closest?.('a[href]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const section = a.closest('[data-track-section]')?.getAttribute('data-track-section') || null;

    if (href === '#fit' || href.endsWith('#fit')) {
      return B.track('size_guide_clicked', { from: section || 'buy_box' });
    }
    /* Key on the CTA class, not on "any in-page anchor". The section bodies are richtext that
       Erez edits in the theme editor, so a link he adds inside a story row or an FAQ answer
       would otherwise report as a CTA press and quietly inflate the one number this exists to
       read. The selector has to match intent, not shape. */
    if (a.matches('.story__cta, .fit__cta, .assure__cta, .faq__cta, .guide__cta')) {
      return B.track('cta_back_to_buy', { from: section || 'unknown', target: href });
    }
    /* The quiz's outcome moved on 2026-09-17 from an email capture to a route, usually the
       two-pair builder. This is now the quiz's last step, and it matched nothing before:
       `.fit-quiz__cta` is a different class token from `.fit__cta`, so the product-page CTA
       rule never covered it. */
    if (a.matches('.fit-quiz__cta')) {
      return B.track('quiz_outcome_clicked', { destination: href });
    }
    if (a.matches('.bundle__cta, .bundle__card-link')) {
      return B.track('bundle_clicked', { where: a.matches('.bundle__cta') ? 'cta' : 'card' });
    }
    if (href.startsWith('mailto:')) return B.track('contact_clicked', { channel: 'mail' });
    if (href.startsWith('tel:')) return B.track('contact_clicked', { channel: 'phone' });
    if (/wa\.me|whatsapp/.test(href)) return B.track('contact_clicked', { channel: 'whatsapp' });
    if (href.includes('/policies/')) {
      return B.track('policy_opened', { which: href.split('/').filter(Boolean).pop() });
    }
    return undefined;
  }, { passive: true });

  /* Which objection people actually have. The question index, never its text. */
  on(document, 'toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const faq = d.closest('[data-track-section="faq"]');
    const acc = d.matches('[data-testid="accordion-details"]');
    if (!faq && !acc) return;
    const peers = [...(faq || document).querySelectorAll('details')];
    B.track('faq_opened', { where: faq ? 'faq' : 'buy_box', q: peers.indexOf(d) });
  }, true);
}

/* ------------------------------------------------------------------ product */

/** Size and colour choices, add to cart, and a variant that turns out to be unavailable. */
function product(StandardEvents) {
  let lastSize = null;
  let lastColour = null;

  on(document, StandardEvents.productSelect, (e) => {
    const opts = e.detail?.selectedOptions || [];
    opts.forEach((o) => {
      const name = String(o?.name || '');
      const value = String(o?.value ?? '');
      if (!value) return;
      if (/מידה|size/i.test(name) && value !== lastSize) {
        lastSize = value;
        B.track('size_selected', { size: value });
      } else if (/צבע|colou?r/i.test(name) && value !== lastColour) {
        lastColour = value;
        B.track('colour_selected', { colour: value });
      }
    });
    e.detail?.promise?.then?.(safe('variant availability', (res) => {
      if (res?.detail?.resource?.available === false) {
        B.track('variant_unavailable', { size: lastSize, colour: lastColour });
      }
    }))?.catch?.(() => {});
  });

  on(document, StandardEvents.cartLinesUpdate, (e) => {
    if (e.detail?.action !== 'add') return;
    const line = e.detail?.lines?.[0] || {};
    B.track('add_to_cart', {
      variant_id: String(line.merchandiseId || ''),
      quantity: Number(line.quantity || 0),
      context: e.detail?.context || 'unknown',
    });
  });

  on(document, StandardEvents.cartError, () => B.track('cart_error', {}));
}

/* ------------------------------------------------------------------ cart */

function cart() {
  on(document, 'theme-drawer:open', (e) => {
    if (e.target?.id === 'cart-drawer') B.track('cart_opened', {});
  });

  /* begin_checkout has no native theme event. Wallet buttons navigate away immediately, so
     they are recorded on pointerdown rather than click. */
  on(document, 'click', (e) => {
    const btn = e.target.closest?.('button#checkout, button[name="checkout"]');
    if (!btn) return;
    const raw = document.querySelector('[data-cart-subtotal]')?.getAttribute('value');
    B.track('checkout_clicked', {
      subtotal: raw ? Number(raw) : null,
      wallet: false,
    });
  }, { passive: true });

  on(document, 'pointerdown', (e) => {
    if (e.target.closest?.('.additional-checkout-buttons')) {
      B.track('checkout_clicked', { subtotal: null, wallet: true });
    }
  }, { passive: true });
}

/* ------------------------------------------------------------------ video */

/**
 * The teaser autoplays silently for every visitor, so a `play` event on it counts nothing.
 * The press on the play control is the funnel step: it unmutes, seeks to zero and goes
 * fullscreen. That is the moment someone chose to watch.
 */
function video() {
  on(document, 'click', (e) => {
    if (e.target.closest?.('[data-sv-play]')) B.track('video_started', {});
  }, { passive: true });

  /* Two stages exist, one per crop, and CSS shows one; the phone's is the second. Listening
     on the first only (until 2026-09-18) meant no progress was ever recorded from a phone. */
  const hit = new Set();
  document.querySelectorAll('[data-sv-main]').forEach((main) => {
    on(main, 'timeupdate', () => {
      if (!main.duration || !isFinite(main.duration)) return;
      const pct = Math.floor((main.currentTime / main.duration) * 100);
      [25, 50, 75, 95].forEach((mark) => {
        if (pct >= mark && !hit.has(mark)) {
          hit.add(mark);
          B.track('video_progress', { pct: mark === 95 ? 100 : mark });
        }
      });
    }, { passive: true });
    on(main, 'volumechange', () => {
      if (!main.muted && !hit.has('unmuted')) { hit.add('unmuted'); B.track('video_unmuted', {}); }
    });

    /* What happened when someone taps a playing film (2026-10-07). A tap on the phone's own
       video controls changes nothing in the page, so Clarity files it as a dead click and the
       recording cannot say whether the film froze or the visitor paused it. These name it, at
       the moment it happened, on the recording's timeline. Clarity keeps the name only; the
       second of the film goes to Shopify's bus with it. */
    const at = () => ({ at: Math.round(main.currentTime || 0) });
    on(main, 'pause', () => {
      if (main.ended || main.hidden || (main.currentTime || 0) < 1) return;
      B.track(document.visibilityState === 'hidden' ? 'video_left' : 'video_paused', at());
    });

    /* A stall is waiting that lasts. Under 3 s is the stream changing rungs, not a freeze. */
    let stall = 0;
    on(main, 'waiting', () => {
      if ((main.currentTime || 0) < 0.5 || stall) return;
      stall = setTimeout(() => {
        stall = 0;
        if (!main.paused && main.readyState < 3) B.track('video_stalled', at());
      }, 3000);
    });
    on(main, 'playing', () => { clearTimeout(stall); stall = 0; });

    /* Closing the full-screen player before the end. iPhone fires its own event; everyone
       else leaves document fullscreen. */
    on(main, 'webkitendfullscreen', () => { if (!main.ended) B.track('video_closed', at()); });
    on(document, 'fullscreenchange', () => {
      if (document.fullscreenElement || main.hidden || main.ended || !main.currentTime) return;
      if (main.closest('[data-sv-stage]')?.offsetParent === null) return;
      B.track('video_closed', at());
    });
  });
}

/* ------------------------------------------------------------------ quiz */

function quiz() {
  ['started', 'answered', 'completed', 'restarted', 'lead_submitted'].forEach((name) => {
    on(document, 'fit-quiz:' + name, (e) => B.track('quiz_' + name, e.detail || {}));
  });
}

/* ------------------------------------------------------------------ search */

function search() {
  on(document, 'submit', (e) => {
    const form = e.target;
    if (!form?.matches?.('form[action*="/search"]')) return;
    const q = form.querySelector('input[name="q"]')?.value || '';
    B.track('search', { length: q.trim().length });
  });
}

/* ------------------------------------------------------------------ start */

function start() {
  loadClarity(B.cfg.clarity);
  if (!B.cfg.events) return;
  source();
  sections();
  clicks();
  cart();
  video();
  quiz();
  search();

  /* The standard-events module comes off Shopify's CDN. Imported dynamically so that if it is
     unreachable the DOM-level tracking above still runs, instead of the whole file failing. */
  import('@shopify/events')
    .then(({ StandardEvents }) => product(StandardEvents))
    .catch((err) => {
      if (B.cfg.debug) console.warn('[balanced] standard events unavailable', err);
    });

  if (B.cfg.debug) console.log('[balanced] tracking started', B.cfg);
}

if (B) whenAllowed(safe('start', start));
